import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel, FieldSet, FieldLegend } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { BirthRuleConfiguration, BirthTimerProtocol } from './birthRules';
export function BirthTimerConfigEditor({
  value,
  onChange,
}: {
  value?: BirthRuleConfiguration;
  onChange: (value: BirthRuleConfiguration) => void;
}) {
  const current = value ?? { clinically_reviewed: false, rules: [], timers: [] };
  const update = (index: number, patch: Partial<BirthTimerProtocol>) =>
    onChange({
      ...current,
      clinically_reviewed: false,
      timers: current.timers.map((timer, i) => (i === index ? { ...timer, ...patch } : timer)),
    });
  return (
    <FieldSet>
      <FieldLegend>Reviewed timer protocols</FieldLegend>
      <p className="text-sm text-muted-foreground">
        Configure observed-event bundles, trigger milestones and intervals from the practice protocol. No medical timing
        defaults are enabled.
      </p>
      {current.timers.map((timer, index) => (
        <FieldSet key={index}>
          <FieldLegend>{timer.label || `Timer ${index + 1}`}</FieldLegend>
          <Field orientation="horizontal">
            <Checkbox
              id={`timer-${index}-enabled`}
              checked={timer.enabled}
              onCheckedChange={(v) => update(index, { enabled: v === true })}
            />
            <FieldLabel htmlFor={`timer-${index}-enabled`}>Enabled</FieldLabel>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['id', 'label', 'citation', 'start'] as const).map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`timer-${index}-${key}`}>
                  {key === 'start' ? 'Start event or milestone' : key === 'citation' ? 'Protocol source' : key}
                </FieldLabel>
                <Input
                  id={`timer-${index}-${key}`}
                  className="min-h-11"
                  value={timer[key]}
                  onChange={(e) => update(index, { [key]: e.target.value })}
                />
              </Field>
            ))}
            <Field>
              <FieldLabel>Subject</FieldLabel>
              <Select
                value={timer.subject}
                onValueChange={(v) => update(index, { subject: v as BirthTimerProtocol['subject'] })}>
                <SelectTrigger className="min-h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {['MOTHER', 'FETUS', 'NEWBORN'].map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            {(['stop', 'observations'] as const).map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`timer-${index}-${key}`}>
                  {key === 'stop' ? 'Stop events' : 'Required observations'} (comma separated)
                </FieldLabel>
                <Input
                  className="min-h-11"
                  id={`timer-${index}-${key}`}
                  value={timer[key].join(', ')}
                  onChange={(e) =>
                    update(index, {
                      [key]: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Field>
            ))}
            {(['interval_minutes', 'warning_fraction', 'max_duration_minutes', 'start_after_minutes'] as const).map(
              (key) => (
                <Field key={key}>
                  <FieldLabel htmlFor={`timer-${index}-${key}`}>{key.replaceAll('_', ' ')}</FieldLabel>
                  <Input
                    id={`timer-${index}-${key}`}
                    type="number"
                    step="any"
                    className="min-h-11"
                    value={timer[key] ?? ''}
                    onChange={(e) =>
                      update(index, {
                        [key]:
                          e.target.value === ''
                            ? key === 'max_duration_minutes'
                              ? null
                              : undefined
                            : Number(e.target.value),
                      })
                    }
                  />
                </Field>
              ),
            )}
            <Field>
              <FieldLabel htmlFor={`timer-${index}-apgar`}>Match Apgar minute (optional)</FieldLabel>
              <Input
                id={`timer-${index}-apgar`}
                type="number"
                min={0}
                step={1}
                className="min-h-11"
                value={(timer.observation_match?.minute as number) ?? ''}
                onChange={(e) =>
                  update(index, { observation_match: e.target.value === '' ? {} : { minute: Number(e.target.value) } })
                }
              />
            </Field>
          </div>
          <Field orientation="horizontal">
            <Checkbox
              id={`timer-${index}-once`}
              checked={timer.once ?? false}
              onCheckedChange={(v) => update(index, { once: v === true })}
            />
            <FieldLabel htmlFor={`timer-${index}-once`}>
              Complete after the matching observation (one-time reminder)
            </FieldLabel>
          </Field>
          <Button
            type="button"
            variant="outline"
            className="min-h-11 self-start"
            onClick={() =>
              onChange({ ...current, clinically_reviewed: false, timers: current.timers.filter((_, i) => i !== index) })
            }>
            Remove timer
          </Button>
        </FieldSet>
      ))}
      <Button
        type="button"
        variant="outline"
        className="min-h-11 self-start"
        onClick={() =>
          onChange({
            ...current,
            clinically_reviewed: false,
            timers: [
              ...current.timers,
              {
                id: '',
                label: '',
                enabled: false,
                citation: '',
                subject: 'MOTHER',
                start: '',
                stop: [],
                observations: [],
                interval_minutes: Number.NaN,
                warning_fraction: Number.NaN,
                max_duration_minutes: null,
              },
            ],
          })
        }>
        Add timer protocol
      </Button>
    </FieldSet>
  );
}
